import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Button, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Card, CardContent, CardActionArea, CircularProgress,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import FolderIcon from "@mui/icons-material/Folder";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import GroupIcon from "@mui/icons-material/Group";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CloseIcon from "@mui/icons-material/Close";
import { AuthContext } from "../context/AuthContext";
import Header from "../components/Header";
import { useSnack } from "../hooks/useSnack";
import {
  getMyAlbums, createAlbum, updateAlbum, deleteAlbum,
  getMyGroups, createGroup, updateGroup, deleteGroup,
  addMemberToGroup, removeMemberFromGroup,
} from "../service/albumApi";

// ─── Dialogue création/édition album ─────────────────────────────────────────
function AlbumDialog({ open, onClose, onSave, initial }) {
  const [name, setName] = useState(initial?.name || "");
  const [desc, setDesc] = useState(initial?.description || "");
  const theme = useTheme();
  const col = theme.custom.mymodal;

  useEffect(() => {
    if (open) { setName(initial?.name || ""); setDesc(initial?.description || ""); }
  }, [open, initial]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { bgcolor: col.main, color: col.text } }}>
      <DialogTitle sx={{ color: col.text }}>{initial ? "Modifier l'album" : "Nouvel album"}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus fullWidth label="Nom de l'album" value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && name.trim() && onSave(name.trim(), desc.trim())}
          sx={{ mt: 1, mb: 2, "& input": { color: col.text }, "& label": { color: col.text + "99" },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: col.text + "44" } }}
        />
        <TextField
          fullWidth label="Description (optionnel)" value={desc} multiline rows={2}
          onChange={(e) => setDesc(e.target.value)}
          sx={{ "& textarea": { color: col.text }, "& label": { color: col.text + "99" },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: col.text + "44" } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: col.text }}>Annuler</Button>
        <Button onClick={() => name.trim() && onSave(name.trim(), desc.trim())}
          variant="contained" sx={{ bgcolor: col.button }}>
          {initial ? "Enregistrer" : "Créer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Dialogue gestion d'un groupe ────────────────────────────────────────────
function GroupDialog({ open, onClose, group, onUpdated }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const col = theme.custom.mymodal;
  const { showSnack, Snack } = useSnack();

  const handleAddMember = async () => {
    if (!email.trim()) return;
    setLoading(true);
    try {
      const member = await addMemberToGroup(group.group_id, email.trim());
      onUpdated({
        ...group,
        members: [...(group.members || []), member],
      });
      setEmail("");
      showSnack(`${member.users_pseudo} ajouté !`, "success");
    } catch (e) {
      showSnack(e.message, "error");
    } finally { setLoading(false); }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await removeMemberFromGroup(group.group_id, userId);
      onUpdated({ ...group, members: group.members.filter((m) => m.users_ID !== userId) });
    } catch (e) {
      showSnack(e.message, "error");
    }
  };

  if (!group) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: col.main, color: col.text } }}>
        <DialogTitle sx={{ color: col.text, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <GroupIcon />
            {group.name}
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: col.text }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: col.text, opacity: 0.7, mb: 2, fontSize: "0.85rem" }}>
            {group.members?.length || 0} membre(s)
          </Typography>

          {/* Liste des membres */}
          {(group.members || []).map((m) => (
            <Box key={m.users_ID} sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              py: 0.8, borderBottom: `1px solid ${col.text}22`,
            }}>
              <Box>
                <Typography sx={{ color: col.text, fontSize: "0.9rem" }}>{m.users_pseudo}</Typography>
                <Typography sx={{ color: col.text, opacity: 0.5, fontSize: "0.75rem" }}>{m.users_email}</Typography>
              </Box>
              <IconButton size="small" onClick={() => handleRemoveMember(m.users_ID)}
                sx={{ color: col.text, opacity: 0.5, "&:hover": { opacity: 1, color: "#f44336" } }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}

          {/* Ajouter un membre */}
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <TextField
              size="small" fullWidth placeholder="Email de l'utilisateur" value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddMember()}
              sx={{ "& input": { color: col.text }, "& .MuiOutlinedInput-notchedOutline": { borderColor: col.text + "44" } }}
            />
            <Button variant="contained" onClick={handleAddMember} disabled={loading || !email.trim()}
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <PersonAddIcon />}
              sx={{ bgcolor: col.button, whiteSpace: "nowrap" }}>
              Ajouter
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      {Snack}
    </>
  );
}

// ─── Page Mes Albums ──────────────────────────────────────────────────────────
export default function MyAlbums() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { state: user } = useContext(AuthContext);
  const { showSnack, Snack } = useSnack();
  const col = theme.custom.mymodal;

  const [albums, setAlbums] = useState([]);
  const [groups, setGroups] = useState({ owned: [], member_of: [] });
  const [loading, setLoading] = useState(true);

  // Dialogues albums
  const [albumDialogOpen, setAlbumDialogOpen] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);

  // Dialogues groupes
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [editingGroup, setEditingGroup] = useState(null);
  const [memberDialogGroup, setMemberDialogGroup] = useState(null);

  useEffect(() => {
    if (!user) { navigate("/connexion"); return; }
    Promise.all([getMyAlbums(), getMyGroups()])
      .then(([a, g]) => { setAlbums(a); setGroups(g); })
      .catch((e) => showSnack(e.message, "error"))
      .finally(() => setLoading(false));
  }, [user, navigate]); // eslint-disable-line

  // ── Albums ──
  const handleSaveAlbum = async (name, description) => {
    try {
      if (editingAlbum) {
        const updated = await updateAlbum(editingAlbum.album_id, name, description);
        setAlbums((prev) => prev.map((a) => a.album_id === updated.album_id ? { ...a, name: updated.name, description: updated.description } : a));
        showSnack("Album mis à jour !", "success");
      } else {
        const created = await createAlbum(name, description);
        setAlbums((prev) => [{ ...created, media_count: 0 }, ...prev]);
        showSnack("Album créé !", "success");
      }
      setAlbumDialogOpen(false);
      setEditingAlbum(null);
    } catch (e) { showSnack(e.message, "error"); }
  };

  const handleDeleteAlbum = async (album) => {
    if (!window.confirm(`Supprimer l'album "${album.name}" ? Les photos ne seront pas supprimées.`)) return;
    try {
      await deleteAlbum(album.album_id);
      setAlbums((prev) => prev.filter((a) => a.album_id !== album.album_id));
      showSnack("Album supprimé.", "success");
    } catch (e) { showSnack(e.message, "error"); }
  };

  // ── Groupes ──
  const handleCreateGroup = async () => {
    if (!groupName.trim()) return;
    try {
      const created = await createGroup(groupName.trim());
      setGroups((prev) => ({ ...prev, owned: [created, ...prev.owned] }));
      setGroupName("");
      setGroupDialogOpen(false);
      showSnack("Groupe créé !", "success");
    } catch (e) { showSnack(e.message, "error"); }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Supprimer le groupe "${group.name}" ?`)) return;
    try {
      await deleteGroup(group.group_id);
      setGroups((prev) => ({ ...prev, owned: prev.owned.filter((g) => g.group_id !== group.group_id) }));
      showSnack("Groupe supprimé.", "success");
    } catch (e) { showSnack(e.message, "error"); }
  };

  const handleGroupUpdated = (updatedGroup) => {
    setGroups((prev) => ({
      ...prev,
      owned: prev.owned.map((g) => g.group_id === updatedGroup.group_id ? updatedGroup : g),
    }));
    setMemberDialogGroup(updatedGroup);
  };

  if (loading) {
    return (
      <>
        <Header />
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress sx={{ color: col.text }} />
        </Box>
      </>
    );
  }

  return (
    <>
      <Header />
      <Box sx={{ maxWidth: 1100, mx: "auto", p: { xs: 2, sm: 3 } }}>

        {/* ── Section Albums ── */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
          <Typography variant="h5" sx={{ color: col.text, fontWeight: "bold" }}>
            Mes albums
          </Typography>
          <Button data-tour="tour-new-album" variant="outlined" startIcon={<AddIcon />}
            onClick={() => { setEditingAlbum(null); setAlbumDialogOpen(true); }}
            sx={{ color: col.text, borderColor: col.text + "55", "&:hover": { borderColor: col.text } }}>
            Nouvel album
          </Button>
        </Box>

        {albums.length === 0 ? (
          <Typography sx={{ color: col.text, opacity: 0.5, mb: 4 }}>
            Aucun album. Crée-en un pour commencer à partager !
          </Typography>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }, gap: 2, mb: 4 }}>
            {albums.map((album) => (
              <Card key={album.album_id} sx={{ bgcolor: col.main, border: `1px solid ${col.text}22`, position: "relative" }}>
                <CardActionArea onClick={() => navigate(`/albums/${album.album_id}`)} sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <FolderIcon sx={{ color: col.text, opacity: 0.6, fontSize: "2rem", mt: 0.2 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ color: col.text, fontWeight: 600, fontSize: "1rem",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {album.name}
                      </Typography>
                      {album.description && (
                        <Typography sx={{ color: col.text, opacity: 0.6, fontSize: "0.78rem", mt: 0.3,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {album.description}
                        </Typography>
                      )}
                      <Chip label={`${album.media_count} photo${album.media_count !== 1 ? "s" : ""}`}
                        size="small" sx={{ mt: 0.8, bgcolor: col.text + "18", color: col.text, fontSize: "0.7rem" }} />
                    </Box>
                  </Box>
                </CardActionArea>
                {/* Boutons action */}
                <Box sx={{ position: "absolute", top: 6, right: 6, display: "flex", gap: 0.5 }}>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingAlbum(album); setAlbumDialogOpen(true); }}
                    sx={{ color: col.text, opacity: 0.5, bgcolor: col.main, "&:hover": { opacity: 1 } }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteAlbum(album); }}
                    sx={{ color: col.text, opacity: 0.5, bgcolor: col.main, "&:hover": { opacity: 1, color: "#f44336" } }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Card>
            ))}
          </Box>
        )}

        {/* ── Section Groupes ── */}
        <Box data-tour="tour-groups-title" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
          <Typography variant="h5" sx={{ color: col.text, fontWeight: "bold" }}>
            Mes groupes
          </Typography>
          <Button data-tour="tour-new-group" variant="outlined" startIcon={<AddIcon />}
            onClick={() => setGroupDialogOpen(true)}
            sx={{ color: col.text, borderColor: col.text + "55", "&:hover": { borderColor: col.text } }}>
            Nouveau groupe
          </Button>
        </Box>

        {groups.owned.length === 0 && groups.member_of.length === 0 ? (
          <Typography sx={{ color: col.text, opacity: 0.5 }}>
            Aucun groupe. Crée-en un pour partager facilement avec plusieurs personnes.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {groups.owned.map((g) => (
              <Box key={g.group_id} sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                p: 1.5, border: `1px solid ${col.text}22`, borderRadius: 2, bgcolor: col.main,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <GroupIcon sx={{ color: col.text, opacity: 0.7 }} />
                  <Box>
                    <Typography sx={{ color: col.text, fontWeight: 600 }}>{g.name}</Typography>
                    <Typography sx={{ color: col.text, opacity: 0.5, fontSize: "0.78rem" }}>
                      {g.members?.length || 0} membre(s)
                      {g.members?.length > 0 && ` · ${g.members.map((m) => m.users_pseudo).join(", ")}`}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <IconButton size="small" onClick={() => setMemberDialogGroup(g)}
                    sx={{ color: col.text, opacity: 0.6, "&:hover": { opacity: 1 } }} title="Gérer les membres">
                    <PersonAddIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteGroup(g)}
                    sx={{ color: col.text, opacity: 0.5, "&:hover": { opacity: 1, color: "#f44336" } }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
            {groups.member_of.map((g) => (
              <Box key={g.group_id} sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                p: 1.5, border: `1px solid ${col.text}18`, borderRadius: 2, bgcolor: col.main, opacity: 0.8,
              }}>
                <GroupIcon sx={{ color: col.text, opacity: 0.5 }} />
                <Box>
                  <Typography sx={{ color: col.text, fontSize: "0.9rem" }}>{g.name}</Typography>
                  <Typography sx={{ color: col.text, opacity: 0.45, fontSize: "0.75rem" }}>
                    Groupe de {g.owner?.users_pseudo} · {g.members?.length || 0} membre(s)
                  </Typography>
                </Box>
                <Chip label="Membre" size="small" sx={{ ml: "auto", bgcolor: col.text + "18", color: col.text, fontSize: "0.7rem" }} />
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Dialogue album */}
      <AlbumDialog
        open={albumDialogOpen}
        onClose={() => { setAlbumDialogOpen(false); setEditingAlbum(null); }}
        onSave={handleSaveAlbum}
        initial={editingAlbum}
      />

      {/* Dialogue création groupe */}
      <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { bgcolor: col.main, color: col.text } }}>
        <DialogTitle sx={{ color: col.text }}>Nouveau groupe</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Nom du groupe" value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreateGroup()}
            sx={{ mt: 1, "& input": { color: col.text }, "& label": { color: col.text + "99" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: col.text + "44" } }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDialogOpen(false)} sx={{ color: col.text }}>Annuler</Button>
          <Button onClick={handleCreateGroup} variant="contained" sx={{ bgcolor: col.button }}>Créer</Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue membres groupe */}
      <GroupDialog
        open={!!memberDialogGroup}
        onClose={() => setMemberDialogGroup(null)}
        group={memberDialogGroup}
        onUpdated={handleGroupUpdated}
      />

      {Snack}
    </>
  );
}
